/* eslint-disable */
// VENDORED from learning_brain_poc/rhs4_causedetail.js (W7b / F3, POC commit 6ad5075e). Change: the write-up is read from
// GET /api/v5/brain/cause_detail (cause_key PC_<n>) through brainApi.js with the app's auth headers. V5: summary, mechanism and terms
// are generated on the server from the cause's graph data and retrieved runbooks (no static summary / glossary files); while
// writeup.status is 'pending' the graph part is shown and the write-up is asked again every WRITEUP_POLL_MS.
import { brainGet, BRAIN_ENDPOINTS } from './brainApi';
// Chat v4 right-hand side: the possible-cause detail. The cause NAME (Possible cause card, Causes list) and the cause node on the map
// open a write-up: what it is, its line(s) of diagnosis, where it stands in this chat, every check, the facts for and against it,
// the fix, the track record and the sources. Hover or focus shows the whole write-up at once (long parts folded); a click or tap pins it.
// The write-up comes from GET /api/v5/brain/cause_detail (fetched lazily, cached per pid|category|learning); 'Now in this chat' is client state.
// window.Rhs4CauseDetail = {nameTrigger(pid, name, cls, deps), mapTrigger(node, pid, name, deps), noteState(state), reset()};
// deps = {h, cleanLabel, muted, pct, plural, label, causeInfo, cards, ruledIds, learningOn, state, explainDeps}. Text goes in as text nodes.
(function () {
  'use strict';

  const HTTP_OK = 200;
  const LOAD_DEBOUNCE_MS = 150;               // a hover shorter than this (the pointer passing over) loads nothing
  const WRITEUP_POLL_MS = 1500, WRITEUP_MAX_POLLS = 8;     // the server answers 'pending' after 2.5 s and keeps writing
  const WRITEUP = {pending: 'pending', unavailable: 'unavailable', ready: 'ready'};
  const LEARNED_PREFIX = 'PAT_L';             // learned causes (store.add_candidate ids) have no runbook write-up
  const RUNBOOK_TAG = /\s*\[[^\]]*\brunbook\]\s*/gi;
  const TEXT = {
    unavailable: 'Detail not available for this cause.', loading: 'Loading the write-up…', keep: 'Click to keep open',
    learned: 'No runbook write-up for this learned cause.', now: 'Now in this chat',
    confirmed: 'How it is confirmed', supports: 'Facts that point to it', rulesOut: 'Facts that rule it out', track: 'Track record',
    sources: 'Sources', terms: 'Terms used', why: 'Why it happens: ', how: 'How was it reached?', fromGuide: 'from the guide',
    writing: 'Writing a plain-language summary from the runbooks…', noSummary: 'A plain-language summary could not be written right now.',
    slow: 'The summary is taking longer than usual. Hover again in a moment.', basedOn: 'Summary based on: ',
  };
  const MARK = {done: '✓', failed: '✗', current: '▶', pending: '○'};
  const MARK_WORD = {done: 'Confirmed', failed: 'Not confirmed', current: 'Checking now', pending: 'Not checked yet'};
  const SEM = {required: 'required', alternative: 'alternative', splitter: 'splitter'};   // step_semantics.KIND_*

  const cache = new Map();                    // pid|category|learning -> {status: 'loading'|'ok'|'fail', data}
  let stateSig = null;

  const tidy = (t, d) => d.cleanLabel(String(t === null || t === undefined ? '' : t).replace(RUNBOOK_TAG, ' ').trim());
  const list = v => (Array.isArray(v) ? v : []);
  const obj = v => (v && typeof v === 'object' ? v : {});
  const keyOf = (pid, d) => {
    const s = d.state();
    return [pid, s.category || '', d.learningOn() ? 1 : 0].join('|');
  };
  const popKey = pid => 'cause-detail-' + pid;

  // ---------- data ----------
  const writeupStatus = data => obj(obj(data).writeup).status || null;
  function load(pid, d) {
    const k = keyOf(pid, d), hit = cache.get(k);
    if (hit && !hit.expired) return hit;
    const entry = {status: 'loading', data: null, polls: 0, expired: false};
    cache.set(k, entry);
    fetchInto(entry, pid, k);
    return entry;
  }
  function fetchInto(entry, pid, k) {
    brainGet(BRAIN_ENDPOINTS.CAUSE_DETAIL, {cause_key: pid})      // V5: GET /api/v5/brain/cause_detail?session_id=&cause_key=PC_<n>
      .then(r => (r.status === HTTP_OK && r.data && r.data.kind !== 'error' ? r.data : Promise.reject(new Error('HTTP ' + r.status))))
      .then(data => {
        entry.status = data && typeof data === 'object' ? 'ok' : 'fail';
        entry.data = obj(data);
        if (writeupStatus(entry.data) !== WRITEUP.pending) return;
        if (entry.polls >= WRITEUP_MAX_POLLS) { entry.expired = true; return; }   // the next hover asks again
        entry.polls += 1;
        setTimeout(() => { if (cache.get(k) === entry) fetchInto(entry, pid, k); }, WRITEUP_POLL_MS);
      })
      .catch(() => { if (entry.status === 'loading') entry.status = 'fail'; })   // a failed re-ask keeps the part already shown
      .finally(() => { if (cache.get(k) === entry && window.Rhs4Explain) window.Rhs4Explain.refresh(popKey(pid)); });
  }
  // the write-up's own state line (generated summary pending, failed, or its cited sources)
  function writeupLine(entry, d) {
    const data = obj(entry && entry.data), st = writeupStatus(data), cited = list(obj(data.writeup).cited);
    if (st === WRITEUP.pending) return d.h('p', {class: 'v4-cd-skel', 'aria-live': 'polite'}, entry.expired ? TEXT.slow : TEXT.writing);
    if (st === WRITEUP.unavailable) return d.muted(TEXT.noSummary);
    if (st === WRITEUP.ready && cited.length) return d.muted(TEXT.basedOn + cited.map(t => String(t).replace(RUNBOOK_TAG, ' ').trim()).join('; ') + '.');
    return null;
  }
  // server data changes only on publish, a fix outcome, an undo, a learning or category switch: then every cached write-up is dropped
  function noteState(s) {
    const st = s || {};
    const sig = JSON.stringify([st.taught, st.lastLearned || null, st.neoStale, st.resolved, st.escalated, st.firstFailed, st.overrideUndo, st.awaitingFix || null, st.category || '']);
    if (sig !== stateSig) { stateSig = sig; cache.clear(); }
  }
  function reset() { cache.clear(); stateSig = null; }

  // ---------- glossary: each term in a shown text becomes a dotted <abbr>; text nodes only ----------
  function glossary(data) {
    const terms = list(data.terms).filter(t => t && t.term);
    if (!terms.length) return null;
    const by = {};
    terms.forEach(t => { by[String(t.term).toLowerCase()] = t; });
    const alt = terms.map(t => String(t.term)).sort((a, b) => b.length - a.length).map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    return {by, re: new RegExp('(?<![\\w])(' + alt + ')(?![\\w])', 'gi'), terms};
  }
  function gloss(text, g, d) {
    const t = tidy(text, d);
    if (!g || !t) return t;
    const out = [];
    let at = 0, m;
    g.re.lastIndex = 0;
    while ((m = g.re.exec(t))) {
      const term = g.by[m[0].toLowerCase()] || {};
      if (m.index > at) out.push(t.slice(at, m.index));
      out.push(d.h('abbr', {class: 'v4-cd-term', title: [term.expansion, term.plain].filter(Boolean).join(': ') || null}, m[0]));
      at = m.index + m[0].length;
    }
    if (at < t.length) out.push(t.slice(at));
    return out;
  }

  // ---------- sections ----------
  const fold = (d, title, count, body) => d.h('details', {class: 'v4-cd-fold'},
    d.h('summary', null, title, count !== null ? d.h('span', {class: 'v4-count'}, String(count)) : null), body);
  const block = (d, title, ...kids) => d.h('section', {class: 'v4-cd-sec'}, d.h('h4', {class: 'v4-label'}, title), kids);

  // the confidence (or ruled out) of this cause in this chat, so the hover alone answers 'how likely is it now'
  function nowLine(pid, d) {
    const card = d.cards().find(c => c.pid === pid);
    if (d.ruledIds().has(pid) || (card && card.excluded)) return d.h('p', {class: 'v4-cd-conf'}, 'Ruled out in this chat');
    return card && num(card.post) !== null ? d.h('p', {class: 'v4-cd-conf'}, 'Confidence ' + d.pct(card.post) + '%') : null;
  }
  function head(pid, name, data, d, g) {
    const lods = list(data.lods);
    const learned = String(pid).startsWith(LEARNED_PREFIX);
    const text = data.summary || data.cause;
    return d.h('div', {class: 'v4-cd-head'},
      d.h('h3', {class: 'v4-cd-title'}, tidy(data.label, d) || name), nowLine(pid, d),
      lods.length ? d.h('div', {class: 'v4-cd-lods'}, lods.map(l => d.h('span', {class: 'v4-cd-lod', title: tidy(l.description, d) || null}, tidy(l.label, d)))) : null,
      text ? d.h('p', {class: 'v4-cd-summary'}, gloss(text, g, d)) : null,
      data.mechanism ? d.h('p', {class: 'v4-cd-mech'}, TEXT.why, gloss(data.mechanism, g, d)) : null,
      learned ? d.h('p', {class: 'v4-muted'}, (num(obj(data.record).cases) !== null ? d.plural(num(obj(data.record).cases), 'confirmed conversation') + '. ' : '') + TEXT.learned) : null);
  }
  const num = v => (v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));

  function stepStatus(step, pc, observed) {
    const known = pc && list(pc.steps).find(s => step.key && s.key === step.key);
    if (known && MARK[known.status]) return known.status;
    if (step.confirm_fact && observed.includes(step.confirm_fact)) return 'done';
    if (step.deny_fact && observed.includes(step.deny_fact)) return 'failed';
    return 'pending';
  }
  function nowBlock(pid, d) {
    const s = d.state(), observed = list(s.observed), card = d.cards().find(c => c.pid === pid);
    const pc = list(s.pcList).find(p => p.pid === pid);
    if (!card && !pc) return block(d, TEXT.now, d.muted('Not among the possible causes of this chat yet.'));
    const ruled = d.ruledIds().has(pid) || (card && card.excluded);
    const kids = [];
    if (ruled) kids.push(d.h('p', null, 'Ruled out in this chat.'));
    else if (card && num(card.post) !== null) {
      kids.push(d.h('p', null, 'Confidence ' + d.pct(card.post) + '% ', d.h('button', {type: 'button', class: 'v4-link', onclick: e => { e.stopPropagation(); openPct(pid); }}, TEXT.how)));
    }
    const matched = list(card && card.matched).filter(m => observed.includes(m.nid));
    const kills = list(card && card.kills).filter(k => observed.includes(k.nid));
    if (matched.length) kids.push(d.h('ul', {class: 'v4-cd-facts'}, matched.map(m => d.h('li', null, d.h('span', {class: 'v4-cd-ok', 'aria-label': 'Points to it'}, MARK.done), ' ', tidy(m.label, d) || d.label(m.nid)))));
    if (kills.length) kids.push(d.h('ul', {class: 'v4-cd-facts'}, kills.map(k => d.h('li', null, d.h('span', {class: 'v4-cd-no', 'aria-label': 'Argues against it'}, MARK.failed), ' ', d.label(k.nid), k.why ? d.h('span', {class: 'v4-muted'}, ' · ' + tidy(k.why, d)) : null))));
    const steps = list(pc && pc.steps);
    if (steps.length) {
      const done = steps.filter(x => x.status === 'done').length, failed = steps.filter(x => x.status === 'failed').length;
      kids.push(d.muted(done + ' of ' + d.plural(steps.length, 'check') + ' confirmed' + (failed ? ', ' + failed + ' not confirmed' : '') + '.'));
    }
    if (!kids.length) kids.push(d.muted('No answer in this chat points to it or against it yet.'));
    return block(d, TEXT.now, kids);
  }
  // the pct popover of this cause in the Causes list ('how was it reached'); opening it closes this one
  function openPct(pid) {
    const prefix = 'v4-xp-' + ('pct-' + pid).replace(/[^\w-]/g, '_') + '-';
    const b = document.querySelector('#v4-rhs [aria-controls^="' + prefix + '"]');
    if (!b) return;
    b.scrollIntoView({block: 'nearest'});
    b.focus();
    b.click();
  }
  function stepTag(step, steps) {
    const sem = obj(step.semantics);
    if (step.kind === 'action') return 'action';
    if (sem.kind === SEM.splitter) return 'routing only';
    if (sem.kind === SEM.alternative) {
      const n = steps.filter(x => obj(x.semantics).kind === SEM.alternative && obj(x.semantics).group === sem.group).length;
      return n > 1 ? 'one of ' + n + ' alternatives' : 'alternative';
    }
    return sem.deciding ? 'deciding' : null;
  }
  function sourceLine(src, d) {
    const s = obj(src);
    const where = [s.runbook, s.section].filter(Boolean).join(' › ') + (num(s.page) !== null ? ', page ' + s.page : '');
    return where ? d.h('div', {class: 'v4-cd-src', title: s.quote ? '“' + String(s.quote) + '”' : null}, where.replace(RUNBOOK_TAG, ' ').trim()) : null;
  }
  function checksBlock(pid, data, d, g) {
    const steps = list(data.steps).slice().sort((a, b) => (num(a.order) || 0) - (num(b.order) || 0));
    if (!steps.length) return block(d, TEXT.confirmed, d.muted('No checks are stored for this cause.'));
    const s = d.state(), pc = list(s.pcList).find(p => p.pid === pid), observed = list(s.observed);
    return block(d, TEXT.confirmed, d.h('ol', {class: 'v4-cd-steps'}, steps.map(step => {
      const st = stepStatus(step, pc, observed), tag = stepTag(step, steps);
      const when = [step.confirm_label ? 'Confirms if ' + tidy(step.confirm_label, d) : '', step.deny_label ? 'rules out if ' + tidy(step.deny_label, d) : ''].filter(Boolean).join(' · ');
      return d.h('li', {class: 'v4-step v4-step-' + st},
        d.h('span', {class: 'v4-mark', 'aria-label': MARK_WORD[st]}, MARK[st]),
        d.h('div', null, d.h('div', {class: 'v4-step-text'}, gloss(step.text, g, d), tag ? d.h('span', {class: 'v4-tag v4-cd-tag'}, tag) : null),
          when ? d.h('div', {class: 'v4-cd-when'}, when) : null, sourceLine(step.source, d)));
    })));
  }
  function supportsFold(data, d) {
    const rows = list(data.supports);
    if (!rows.length) return null;
    return fold(d, TEXT.supports, rows.length, d.h('ul', {class: 'v4-cd-list'}, rows.map(r => d.h('li', null,
      d.h('b', null, tidy(r.label, d) || d.label(r.nid)), r.question ? d.h('span', {class: 'v4-muted'}, ' · ' + tidy(r.question, d)) : null,
      d.h('div', {class: 'v4-muted'}, num(r.seen) ? 'Seen in ' + d.plural(num(r.seen), 'conversation') + ', pointed here in ' + (num(r.wins) || 0) : (r.book ? TEXT.fromGuide : 'not seen in past conversations yet'))))));
  }
  function rulesFold(data, d) {
    const rows = list(data.rules_out);
    if (!rows.length) return null;
    return fold(d, TEXT.rulesOut, rows.length, d.h('ul', {class: 'v4-cd-list'}, rows.map(r => d.h('li', null,
      d.h('b', null, tidy(r.label, d) || d.label(r.nid)), r.why ? d.h('div', {class: 'v4-muted'}, tidy(r.why, d)) : null,
      r.contradicted ? d.h('div', {class: 'v4-muted'}, 'Past conversations contradicted this rule-out, so it only weakens the cause.') : null))));
  }
  function fixBlock(data, d, g) {
    const fix = obj(data.fix), parts = list(fix.parts).filter(p => p && p.text);
    if (!fix.text && !parts.length) return null;
    const tried = num(fix.tried), worked = num(fix.worked);
    return block(d, fix.kind === 'escalation' ? 'Escalation' : 'Fix',
      parts.length > 1 ? d.h('ul', {class: 'v4-cd-list'}, parts.map(p => d.h('li', null, p.runbook ? d.h('b', null, String(p.runbook).replace(RUNBOOK_TAG, ' ').trim() + ': ') : null, gloss(p.text, g, d))))
        : d.h('p', null, gloss(parts.length ? parts[0].text : fix.text, g, d)),
      data.root_cause && data.root_cause.label ? d.muted('Root cause: ' + tidy(data.root_cause.label, d)) : null,
      tried ? d.muted('Tried ' + d.plural(tried, 'time') + ', worked ' + (worked || 0) + '.') : null);
  }
  function trackFold(pid, data, d) {
    const rec = obj(data.record), info = Object.assign({}, d.causeInfo(pid) || {}, rec);
    const card = Object.assign({pid}, d.cards().find(c => c.pid === pid) || {});
    const X = window.Rhs4Explain, xd = Object.assign({}, d.explainDeps(), {causeInfo: () => info});
    const body = d.h('div', null,
      d.h('p', null, (num(rec.cases) !== null ? d.plural(num(rec.cases), 'confirmed conversation') : 'No conversation count') + (rec.last_seen ? ', last seen ' + rec.last_seen : '') + '.'),
      X && X.trackBody ? X.trackBody(card, xd) : null);
    return fold(d, TEXT.track, num(info.trust) !== null ? num(info.trust).toFixed(2) : null, body);
  }
  function sourcesFold(data, d) {
    const src = list(data.sources), merged = list(data.merged_from);
    if (!src.length && !merged.length) return null;
    return fold(d, TEXT.sources, src.length + merged.length, d.h('div', null,
      src.length ? d.h('ul', {class: 'v4-cd-list'}, src.map(x => d.h('li', null, String(x.title || x.slug || '').replace(RUNBOOK_TAG, ' ').trim()))) : null,
      merged.length ? d.h('div', null, d.h('div', {class: 'v4-label'}, 'Merged from'),
        d.h('ul', {class: 'v4-cd-list'}, merged.map(x => d.h('li', null, tidy(x.label, d), x.source ? d.h('span', {class: 'v4-muted'}, ' · ' + String(x.source).replace(RUNBOOK_TAG, ' ').trim()) : null)))) : null));
  }
  function termsFold(g, d) {
    if (!g) return null;
    return fold(d, TEXT.terms, g.terms.length, d.h('dl', {class: 'v4-cd-terms'}, g.terms.map(t => [d.h('dt', null, String(t.term)),
      d.h('dd', null, [t.expansion, t.plain].filter(Boolean).join(': '))])));
  }

  // ---------- the popover body: the whole write-up at once; a click pins it ----------
  function body(pid, name, d, pinned) {
    let entry = cache.get(keyOf(pid, d));
    const missing = !entry || entry.expired;     // expired: the write-up was still pending after the last re-ask
    if (missing && pinned) entry = load(pid, d);
    else if (missing) setTimeout(() => {          // still shown after the debounce: load it; the data arriving re-renders it in place
      const live = document.querySelector('[data-cd-pid="' + CSS.escape(pid) + '"]');
      const pop = live && live.closest('.v4-xp-pop');
      if (pop && !pop.hidden) load(pid, d);
    }, LOAD_DEBOUNCE_MS);
    const data = entry && entry.status === 'ok' ? entry.data : {};
    const g = glossary(data);
    const keep = d.h('p', {class: 'v4-cd-keep'}, TEXT.keep);      // hidden by css once pinned
    const top = head(pid, name, data, d, g);
    if (!entry || entry.status === 'loading') {
      return d.h('div', {class: 'v4-cd', 'data-cd-pid': pid, 'aria-busy': 'true'}, keep, top, d.h('p', {class: 'v4-cd-skel'}, TEXT.loading));
    }
    const failed = entry.status !== 'ok';
    return d.h('div', {class: 'v4-cd', 'data-cd-pid': pid}, keep, top,
      failed ? d.h('p', {class: 'v4-cd-fail'}, TEXT.unavailable) : null,
      failed ? null : writeupLine(entry, d),
      nowBlock(pid, d),
      failed ? null : [checksBlock(pid, data, d, g), fixBlock(data, d, g), supportsFold(data, d), rulesFold(data, d), trackFold(pid, data, d), sourcesFold(data, d), termsFold(g, d)]);
  }
  function trigger(pid, name, d, btnProps, kids, cls, node) {
    const X = window.Rhs4Explain;
    if (!X || !X.popover) return null;
    const wrap = X.popover(d, popKey(pid), 'About the possible cause ' + name, btnProps, kids, pinned => body(pid, name, d, pinned),
      {fixed: true, refresh: true, role: 'dialog', cls: 'v4-cd-pop', trigger: node || null});
    if (cls) wrap.classList.add(...cls.split(' '));
    return wrap;
  }
  // the cause name itself becomes the trigger; it keeps the class (layout) of the element it replaces
  function nameTrigger(pid, name, cls, d) {
    return trigger(pid, name, d, {class: 'v4-cd-name'}, name, cls) || d.h('span', {class: cls}, name);
  }
  // the map's cause node is the trigger (diagmap switches its old tooltip off); the returned holder only carries the panel
  function mapTrigger(node, pid, name, d) {
    return trigger(pid, name, d, null, null, 'v4-cd-holder', node);
  }

  window.Rhs4CauseDetail = {nameTrigger, mapTrigger, noteState, reset};
})();
